import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { BrainwaveControlPoint } from '../../utils/audio/BrainwaveOscillatorNode';
import { Icon } from '../common/Icon';
import { FaTimes } from 'react-icons/fa';

const ContextMenuContainer = styled.div<{ $x: number; $y: number }>`
  position: fixed;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 12px;
  min-width: 200px;
  z-index: 1000;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const MenuHeader = styled.div`
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #444;
`;

const SliderGroup = styled.div`
  margin-bottom: 8px;
`;

const SliderLabel = styled.label`
  display: block;
  color: #ccc;
  font-size: 11px;
  margin-bottom: 4px;
  font-weight: 500;
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Slider = styled.input`
  flex: 1;
  height: 4px;
  background: #444;
  border-radius: 2px;
  outline: none;
  accent-color: #e91e63;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #e91e63;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #e91e63;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
`;

const SliderValue = styled.span`
  color: #e91e63;
  font-size: 10px;
  font-family: monospace;
  min-width: 40px;
  text-align: right;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #444;
`;

const ActionButton = styled.button<{ $variant?: 'danger' | 'primary' }>`
  flex: 1;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  
  ${props => props.$variant === 'danger' ? `
    background: #f44336;
    color: white;
    
    &:hover {
      background: #d32f2f;
    }
  ` : `
    background: #444;
    color: #ccc;
    
    &:hover {
      background: #555;
      color: #fff;
    }
  `}
`;

interface BrainwavePointContextMenuProps {
  x: number;
  y: number;
  point: BrainwaveControlPoint;
  pointIndex: number;
  isBoundaryPoint: boolean;
  onUpdatePoint: (pointIndex: number, updates: Partial<BrainwaveControlPoint>) => void;
  onDeletePoint?: (pointIndex: number) => void;
  onClose: () => void;
}

export const BrainwavePointContextMenu: React.FC<BrainwavePointContextMenuProps> = ({
  x,
  y,
  point,
  pointIndex,
  isBoundaryPoint,
  onUpdatePoint,
  onDeletePoint,
  onClose
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  // Smart positioning to keep menu within viewport
  const getAdjustedPosition = useCallback(() => {
    if (!menuRef.current) return { x, y };

    const menuRect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedX = x;
    let adjustedY = y;

    // Adjust horizontal position if menu would go off-screen
    if (x + menuRect.width > viewportWidth) {
      adjustedX = viewportWidth - menuRect.width - 10;
    }
    if (adjustedX < 10) {
      adjustedX = 10;
    }

    // Adjust vertical position if menu would go off-screen
    if (y + menuRect.height > viewportHeight) {
      adjustedY = viewportHeight - menuRect.height - 10;
    }
    if (adjustedY < 10) {
      adjustedY = 10;
    }

    return { x: adjustedX, y: adjustedY };
  }, [x, y]);

  const position = getAdjustedPosition();

  const handleCarrierFreqChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const carrierFreq = parseFloat(e.target.value);
    onUpdatePoint(pointIndex, { carrierFreq });
  }, [pointIndex, onUpdatePoint]);

  const handleBrainwaveFreqChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const brainwaveFreq = parseFloat(e.target.value);
    onUpdatePoint(pointIndex, { brainwaveFreq });
  }, [pointIndex, onUpdatePoint]);

  const handleAmplitudeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const amplitude = parseFloat(e.target.value);
    onUpdatePoint(pointIndex, { amplitude });
  }, [pointIndex, onUpdatePoint]);

  const handleDelete = useCallback(() => {
    if (onDeletePoint && !isBoundaryPoint) {
      onDeletePoint(pointIndex);
      onClose();
    }
  }, [onDeletePoint, pointIndex, isBoundaryPoint, onClose]);

  const getFrequencyBand = (freq: number): string => {
    if (freq < 4) return 'Delta';
    if (freq < 8) return 'Theta';
    if (freq < 14) return 'Alpha';
    if (freq < 30) return 'Beta';
    return 'Gamma';
  };

  return (
    <ContextMenuContainer 
      ref={menuRef}
      $x={position.x} 
      $y={position.y}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      <MenuHeader>
        Control Point {isBoundaryPoint ? (pointIndex === 0 ? '(Start)' : '(End)') : ''}
      </MenuHeader>

      <SliderGroup>
        <SliderLabel>Carrier Frequency</SliderLabel>
        <SliderContainer>
          <Slider
            type="range"
            min="50"
            max="500"
            step="1"
            value={point.carrierFreq}
            onChange={handleCarrierFreqChange}
          />
          <SliderValue>{point.carrierFreq.toFixed(0)}Hz</SliderValue>
        </SliderContainer>
      </SliderGroup>

      <SliderGroup>
        <SliderLabel>Brainwave Frequency</SliderLabel>
        <SliderContainer>
          <Slider
            type="range"
            min="0.5"
            max="40"
            step="0.1"
            value={point.brainwaveFreq}
            onChange={handleBrainwaveFreqChange}
          />
          <SliderValue>
            {point.brainwaveFreq.toFixed(1)}Hz ({getFrequencyBand(point.brainwaveFreq)})
          </SliderValue>
        </SliderContainer>
      </SliderGroup>

      <SliderGroup>
        <SliderLabel>Amplitude</SliderLabel>
        <SliderContainer>
          <Slider
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={point.amplitude}
            onChange={handleAmplitudeChange}
          />
          <SliderValue>{Math.round(point.amplitude * 100)}%</SliderValue>
        </SliderContainer>
      </SliderGroup>

      <ButtonContainer>
        {!isBoundaryPoint && onDeletePoint && (
          <ActionButton $variant="danger" onClick={handleDelete}>
            <Icon icon={FaTimes} />
            Remove
          </ActionButton>
        )}
        <ActionButton onClick={onClose}>
          Close
        </ActionButton>
      </ButtonContainer>
    </ContextMenuContainer>
  );
};

export default BrainwavePointContextMenu;